'use client';

import React, { useMemo, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { Button, Card, CardBody, Chip, Tabs, Tab } from '@heroui/react';
import { Typography } from '../../../components';
import { ResponsiveTooltip } from '../../../components/ui';
import { usePageBreadcrumbs } from '../../../hooks/usePageBreadcrumbs';
import { useAccount } from '../../../contexts/AccountContext';
import { useAccountPageTitle } from '../../../hooks/useAccountPageTitle';
import {
  CredentialsTab,
  PublicContentTab,
  WorkContentTab,
  OfferTab,
  CampaignsTab,
  InvoicesTab,
  GuaranteeTab,
} from './components';
import Avatar from './avatar2.png';
import clientAccService from '../../../api/client/clientAccService';
import clientRevshareService from '../../../api/client/clientRevshareService';
import { useTranslations } from '../../../contexts/translation.context';

// Функция для форматирования даты будет создана внутри компонента

interface AccountPageProps {}

const AccountPage: React.FC<AccountPageProps> = () => {
  const { id, tab } = useParams<{ id: string; tab?: string }>();
  const navigate = useNavigate();
  const { refreshAccounts } = useAccount();
  const [accountData, setAccountData] = React.useState<any>({});
  const [campaignsCount, setCampaignsCount] = useState(0);
  const [billsCount, setBillsCount] = useState(0);
  const { t } = useTranslations();

  // Функция для форматирования даты
  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');

      return `${t('accountPage.updated')} ${day}.${month}.${year}, ${hours}:${minutes}`;
    } catch (error) {
      console.error('Ошибка при форматировании даты:', error);
      return `${t('accountPage.updated')} 07.06.2025, 16:58`; // fallback
    }
  };

  // Определяем активный таб из URL или устанавливаем по умолчанию
  const activeTab = tab || 'access';
  const getData = async () => {
    try {
      const { data } = await clientAccService.getModelById(id);

      // Используем internal id для запросов
      const internalModelId = data?._id || data?.id;

      // Загружаем кол-во кампаний отдельным запросом
      let campaignsCountFromApi = 0;
      if (internalModelId) {
        try {
          const campaignsResponse = await clientRevshareService.getOrders({
            page: 1,
            page_size: 1,
            models: [internalModelId],
          });
          campaignsCountFromApi = campaignsResponse.count || 0;
          setCampaignsCount(campaignsCountFromApi);
        } catch (e) {
          console.error('Error fetching campaigns count:', e);
        }
      }

      // Загружаем кол-во счетов отдельным запросом
      if (internalModelId) {
        try {
          const billsResponse = await clientRevshareService.getBills({
            page: 1,
            page_size: 1,
            model_id: internalModelId,
          });
          setBillsCount(billsResponse.count || 0);
        } catch (e) {
          console.error('Error fetching bills count:', e);
        }
      }

      setAccountData({
        ...data,
        id: id || '1',
        internal_id: data?._id || data?.id,
        of_user_id: data?.of_user_id,
        of_user_name: data?.of_user_name,
        name: data.name,
        username: data.of_user_name,
        avatar: data.avatar,
        offer: data?.offer,
        granted: data?.granted,
        revenue: `$${Number(data?.of_data?.balance)?.toFixed(2)}` || '$0.00',
        fans: data?.of_data?.subscribers || 0,
        performerTop: `${data?.of_data?.overall_popularity_percent || 0}%`,
        email: data?.credentials?.email || '',
        //  password: "•••••••••••••••",
        lastUpdated: data?.credentials?.updated_at ? formatDate(data.credentials.updated_at) : '',
        public_content: data.public_content,
        work_content: {
          nonude: data.work_content?.nonude,
          tt: data.work_content?.tt,
          lingerie: data.work_content?.lingerie,
          lite_nude: data.work_content?.lite_nude,
          full_nude: data.work_content?.full_nude,
        },
        campaignsCount: campaignsCountFromApi || data.campaigns?.length || 0,
        campaignStats: {
          totalFans: 34,
          subscriptions: 0,
          repeatSubscriptions: 0,
          purchases1: 7,
          purchases2: 3,
          purchases5: 0,
          purchases500: 0,
          messages3: 70,
        },
      });

      // Обновляем контекст аккаунтов, чтобы тайтл обновился
      await refreshAccounts();
    } catch (error) {
      console.error('Error fetching account data:', error);
    }
  };
  useEffect(() => {
    getData();
  }, []);
  const validTabs = ['access', 'public', 'work', 'offer', 'campaigns', 'bills', 'guarantee'];

  // Проверяем валидность таба и перенаправляем на дефолтный если нужно
  useEffect(() => {
    if (tab && !validTabs.includes(tab)) {
      navigate(`/client/accounts/${id}/access`, { replace: true });
    } else if (!tab) {
      navigate(`/client/accounts/${id}/access`, { replace: true });
    }
  }, [tab, id, navigate]);

  const handleTabChange = (key: string) => {
    navigate(`/client/accounts/${id}/${key}`);
  };

  const breadcrumbs = useMemo(
    () => [
      { label: t('accountPage.breadcrumb.home'), href: '/' },
      { label: t('accountPage.breadcrumb.accounts'), href: '/client/accounts' },
      { label: accountData.name, isActive: true },
    ],
    [accountData.name, t]
  );

  usePageBreadcrumbs(breadcrumbs);

  // Управление тайтлом страницы аккаунта
  useAccountPageTitle({ accountData });

  // Статистические карточки
  const StatCard = ({ title, value }: { title: string; value: string }) => (
    <Card className="w-full border-0 rounded-none shadow-none">
      <CardBody className="px-4 py-3">
        <Typography
          variant="body2"
          className="text-neutral-800 mb-1 text-sm leading-[1.125rem] font-medium"
        >
          {title}
        </Typography>
        <Typography variant="h6" className="text-neutral-900 font-semibold text-xl leading-6">
          {value}
        </Typography>
      </CardBody>
    </Card>
  );

  // Контент табов
  const renderTabContent = () => {
    switch (activeTab) {
      case 'access':
        return (
          <CredentialsTab
            accountData={{
              email: accountData.email,
              password: accountData.password,
              lastUpdated: accountData.lastUpdated,
              id: accountData.id,
            }}
            onCredentialsUpdated={getData}
          />
        );

      case 'public':
        return <PublicContentTab modelId={id} existingImages={accountData.public_content || []} />;

      case 'work':
        return <WorkContentTab modelId={id} initialData={accountData.work_content} />;

      case 'offer':
        return <OfferTab modelId={id} accountData={accountData} />;

      case 'campaigns':
        return (
          <CampaignsTab
            accountData={accountData}
            modelId={accountData.internal_id || id}
            onCountChange={setCampaignsCount}
          />
        );

      case 'bills':
        return <InvoicesTab modelId={accountData.internal_id || id} />;

      case 'guarantee':
        return (
          <GuaranteeTab
            modelId={accountData.internal_id || id}
            initialData={{ modelName: accountData.name, granted: accountData.granted }}
            onUpdate={getData}
          />
        );

      default:
        return (
          <CredentialsTab
            accountData={{
              email: accountData.email,
              password: accountData.password,
              lastUpdated: accountData.lastUpdated,
            }}
            onCredentialsUpdated={getData}
          />
        );
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-6 md:px-6 md:py-5 h-full flex flex-col">
        <Typography variant="h5" className="mb-8 max-md:hidden">
          {accountData.name}
        </Typography>
        {/* Main Content */}
        <div className="flex md:flex-row flex-col gap-6 md:flex-1 md:h-screen">
          {/* Left Sidebar */}

          <div className="md:w-56 flex-shrink-0 grid md:grid-cols-1 grid-cols-2 md:gap-0 gap-4 md:flex md:flex-col">
            {/* Avatar and Info */}
            <div className="md:mb-6">
              <div className="size-[9.4375rem] md:size-[12.5rem] rounded-xl overflow-hidden mb-4">
                <img
                  src={accountData.avatar}
                  alt={accountData.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <Typography variant="h6" className="text-auth-ink font-medium text-xl mb-1">
                  {accountData.name}
                </Typography>
                <a
                  target="__blank"
                  href={`https://onlyfans.com/${accountData.username}`}
                  className="text-blue-600 hover:underline flex items-center gap-1"
                >
                  <Typography variant="body2" className="!text-partner">
                    @{accountData.username}
                  </Typography>
                </a>
              </div>
            </div>

            {/* Stats Cards */}
            <ResponsiveTooltip
              content={t('accountPage.stats.ofStatsTooltip')}
              placement="bottom"
              classNames={{ content: 'max-w-xs' }}
            >
              <div className="flex flex-col border border-gray-90 rounded-3.5 overflow-hidden">
                <StatCard title={t('accountPage.stats.revenue')} value={accountData.revenue} />
                <div className="border-t !border-gray-90"></div>
                <StatCard title={t('accountPage.stats.fans')} value={accountData.fans} />
                <div className="border-t !border-gray-90"></div>
                <StatCard
                  title={t('accountPage.stats.performerTop')}
                  value={accountData.performerTop}
                />
              </div>
            </ResponsiveTooltip>
          </div>

          {/* Right Content */}
          <div className="md:flex-1 ">
            {/* Tabs */}
            <div className="mb-4 md:mb-8 md:px-0 md:mx-0 -mx-4 px-4 max-md:overflow-x-auto scroller-none">
              <div className="bg-auth-surface rounded-xl w-fit">
                <Tabs
                  selectedKey={activeTab}
                  onSelectionChange={(key) => handleTabChange(key as string)}
                  variant="light"
                  classNames={{
                    tabList: 'gap-1',
                    cursor: 'bg-white rounded-lg shadow-sm',
                    tab: 'px-3 py-1 text-sm font-normal data-[selected=true]:text-black text-auth-muted',
                    tabContent: 'group-data-[selected=true]:text-black',
                  }}
                >
                  {(() => {
                    const offerStatus = accountData?.offer?.status;

                    let offerChipClass = 'bg-auth-strength-none/40 text-auth-muted text-xs';
                    let offerChipText = t('accountPage.tabs.offerMissing') || 'Нет оффера';

                    if (offerStatus === 'active') {
                      offerChipClass = 'bg-success/20 text-success text-xs';
                      offerChipText = t('accountPage.tabs.offerActive') || 'Оффер активен';
                    } else if (offerStatus === 'moderation') {
                      offerChipClass = 'bg-warning/20 text-warning text-xs';
                      offerChipText = t('accountPage.tabs.offerModeration') || 'Оффер на модерации';
                    } else if (offerStatus === 'hidden') {
                      offerChipClass = 'bg-auth-strength-none/40 text-auth-muted text-xs';
                      offerChipText = t('accountPage.tabs.offerHidden') || 'Оффер скрыт';
                    }

                    return (
                      <React.Fragment key="offer-tab-title-fragment">
                        <Tab key="access" title={t('accountPage.tabs.access')} />
                        <Tab key="public" title={t('accountPage.tabs.publicContent')} />
                        <Tab key="work" title={t('accountPage.tabs.workContent')} />
                        <Tab
                          key="offer"
                          title={
                            <div className="flex items-center gap-2">
                              <span>{t('accountPage.tabs.offer')}</span>
                              <Chip size="sm" className={offerChipClass}>
                                {offerChipText}
                              </Chip>
                            </div>
                          }
                        />
                      </React.Fragment>
                    );
                  })()}
                  <Tab
                    key="campaigns"
                    title={
                      <div className="flex items-center gap-2 group relative">
                        <span>{t('accountPage.tabs.campaigns')}</span>
                        <Chip
                          size="sm"
                          className="bg-auth-strength-none/40 text-auth-muted text-xs"
                        >
                          {campaignsCount || accountData.campaignsCount || 0}
                        </Chip>
                      </div>
                    }
                    className="hover:bg-white/50 transition-colors"
                  />
                  <Tab
                    key="bills"
                    title={
                      <div className="flex items-center gap-2">
                        <span>{t('accountPage.tabs.bills')}</span>
                        <Chip
                          size="sm"
                          className="bg-auth-strength-none/40 text-auth-muted text-xs"
                        >
                          {billsCount}
                        </Chip>
                      </div>
                    }
                  />
                  <Tab key="guarantee" title={t('accountPage.tabs.guarantee')} />
                </Tabs>
              </div>
            </div>

            {/* Tab Content */}
            <div className="md:h-full">{renderTabContent()}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountPage;
